import { useEffect, useRef, useState } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { PlayIcon } from '../icons/PlayIcon';
import { PauseIcon } from '../icons/PauseIcon';
import { UndoIcon } from '../icons/UndoIcon';
import { VolumeIcon } from '../icons/VolumeIcon';
import { CaptionsIcon } from '../icons/CaptionsIcon';
import { FullscreenIcon } from '../icons/FullscreenIcon';
import { ClockIcon } from '../icons/ClockIcon';
import { RefreshIcon } from '../icons/RefreshIcon';
import { CheckIcon } from '../icons/CheckIcon';

const PLAYBACK_RATES = [0.8, 1, 1.25, 1.5];
const formatRate = (r: number) => r.toString().replace('.', ',');

export type VideoSegment = {
  startSec: number;
  endSec: number;
};

type VideoPlayerProps = {
  title: string;
  duration?: string;
  slideCount: number;
  currentSlide: number;
  onSlideClick?: (slide: number) => void;
  playSignal?: number;
  subStepTimestamp?: string;
  subStepTitle?: string;
  youtubeId?: string;
  videoUrl?: string;
  segments?: VideoSegment[];
  mediaUrls?: string[];
};

const SLIDE_DURATION_MS = 6000;

declare global {
  interface Window {
    YT?: {
      Player: new (el: HTMLElement | string, options: unknown) => YTPlayer;
    };
    onYouTubeIframeAPIReady?: () => void;
  }
}

type YTPlayer = {
  playVideo: () => void;
  pauseVideo: () => void;
  seekTo: (seconds: number, allowSeekAhead?: boolean) => void;
  getCurrentTime: () => number;
  destroy: () => void;
};

let youtubeAPIPromise: Promise<void> | null = null;
const loadYouTubeAPI = () => {
  if (youtubeAPIPromise) return youtubeAPIPromise;
  youtubeAPIPromise = new Promise<void>(resolve => {
    if (window.YT?.Player) {
      resolve();
      return;
    }
    const previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      previous?.();
      resolve();
    };
    if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(tag);
    }
  });
  return youtubeAPIPromise;
};

export function VideoPlayer({
  title,
  duration = '1m',
  slideCount,
  currentSlide,
  onSlideClick,
  playSignal,
  subStepTimestamp,
  subStepTitle,
  youtubeId,
  videoUrl,
  segments,
  mediaUrls,
}: VideoPlayerProps) {
  const [hasStarted, setHasStarted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [flashIcon, setFlashIcon] = useState<'play' | 'pause' | null>(null);
  const [flashKey, setFlashKey] = useState(0);
  const [hoveredCursor, setHoveredCursor] = useState<{ slide: number; x: number } | null>(null);
  const [seekTo, setSeekTo] = useState<{ slide: number; offset: number } | null>(null);
  const [videoEnded, setVideoEnded] = useState(false);
  const [ytTime, setYtTime] = useState<number>(segments?.[0]?.startSec ?? 0);
  const [volume, setVolume] = useState(1);
  const [previousVolume, setPreviousVolume] = useState(1);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [speedMenuOpen, setSpeedMenuOpen] = useState(false);
  const isMuted = volume === 0;

  const handleVolumeChange = (next: number) => {
    const clamped = Math.max(0, Math.min(1, next));
    setVolume(clamped);
    if (clamped > 0) setPreviousVolume(clamped);
  };

  const toggleMute = () => {
    if (isMuted) {
      setVolume(previousVolume || 1);
    } else {
      setVolume(0);
    }
  };

  const skip = (deltaSec: number) => {
    if (!segments) return;
    const minTime = segments[0].startSec;
    const maxTime = segments[segments.length - 1].endSec - 0.05;
    if (isHtmlVideo && videoElementRef.current) {
      const video = videoElementRef.current;
      const newTime = Math.max(minTime, Math.min(maxTime, video.currentTime + deltaSec));
      video.currentTime = newTime;
      if (videoEnded) {
        setVideoEnded(false);
        video.play().catch(() => {});
        setIsPlaying(true);
      }
    }
    if (isYoutube && playerRef.current) {
      const player = playerRef.current;
      const newTime = Math.max(minTime, Math.min(maxTime, player.getCurrentTime() + deltaSec));
      player.seekTo(newTime, true);
      if (videoEnded) {
        setVideoEnded(false);
        player.playVideo();
        setIsPlaying(true);
      }
    }
  };
  const lastPlaySignal = useRef(playSignal);
  const playerRef = useRef<YTPlayer | null>(null);
  const playerReadyRef = useRef(false);
  const reportedSlideRef = useRef(currentSlide);
  const ytContainerIdRef = useRef(`yt-${Math.random().toString(36).slice(2)}`);
  const ytContainerId = ytContainerIdRef.current;
  const videoElementRef = useRef<HTMLVideoElement | null>(null);

  const isYoutube = !!youtubeId && !!segments && segments.length > 0;
  const isHtmlVideo = !!videoUrl && !!segments && segments.length > 0;
  const hasTimedMedia = isYoutube || isHtmlVideo;

  useEffect(() => {
    setVideoEnded(false);
  }, [currentSlide]);

  const effectiveOffset = seekTo?.slide === currentSlide ? seekTo.offset : 0;

  useEffect(() => {
    if (playSignal === undefined || playSignal === lastPlaySignal.current) return;
    lastPlaySignal.current = playSignal;
    setHasStarted(true);
    setIsPlaying(true);
    if (isYoutube && playerReadyRef.current && segments) {
      if (currentSlide !== reportedSlideRef.current) {
        const seg = segments[currentSlide - 1];
        if (seg) {
          playerRef.current?.seekTo(seg.startSec, true);
          reportedSlideRef.current = currentSlide;
        }
      }
      playerRef.current?.playVideo();
    }
    if (isHtmlVideo && videoElementRef.current && segments) {
      if (currentSlide !== reportedSlideRef.current) {
        const seg = segments[currentSlide - 1];
        if (seg) {
          videoElementRef.current.currentTime = seg.startSec;
          reportedSlideRef.current = currentSlide;
        }
      }
      videoElementRef.current.play().catch(() => {});
    }
  }, [playSignal, isYoutube, isHtmlVideo, segments, currentSlide]);

  useEffect(() => {
    if (!isYoutube) return undefined;
    let cancelled = false;
    let rafId = 0;

    const loop = () => {
      const player = playerRef.current;
      const segs = segments;
      if (player && playerReadyRef.current && segs) {
        const time = player.getCurrentTime();
        if (typeof time === 'number') {
          setYtTime(time);
          const lastSeg = segs[segs.length - 1];
          if (time >= lastSeg.endSec) {
            player.pauseVideo();
            player.seekTo(lastSeg.endSec, true);
            setIsPlaying(false);
            setVideoEnded(true);
          } else {
            const idx = segs.findIndex(s => time >= s.startSec && time < s.endSec);
            const nextSlide = idx >= 0 ? idx + 1 : reportedSlideRef.current;
            if (nextSlide !== reportedSlideRef.current) {
              reportedSlideRef.current = nextSlide;
              onSlideClick?.(nextSlide);
            }
          }
        }
      }
      rafId = requestAnimationFrame(loop);
    };

    loadYouTubeAPI().then(() => {
      if (cancelled) return;
      const el = document.getElementById(ytContainerId);
      if (!el || !window.YT?.Player || !segments) return;
      playerRef.current = new window.YT.Player(el, {
        videoId: youtubeId,
        width: '100%',
        height: '100%',
        playerVars: {
          start: Math.floor(segments[0].startSec),
          controls: 0,
          modestbranding: 1,
          disablekb: 1,
          rel: 0,
          iv_load_policy: 3,
          fs: 0,
          playsinline: 1,
          autoplay: 0,
        },
        events: {
          onReady: () => {
            playerReadyRef.current = true;
          },
          onStateChange: (e: { data: number }) => {
            if (e.data === 1) setIsPlaying(true);
            else if (e.data === 2 || e.data === 0) setIsPlaying(false);
          },
        },
      } as unknown);
    });

    rafId = requestAnimationFrame(loop);

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      try {
        playerRef.current?.destroy();
      } catch {
        // ignore
      }
      playerRef.current = null;
      playerReadyRef.current = false;
    };
  }, [isYoutube, youtubeId, segments, ytContainerId, onSlideClick]);

  useEffect(() => {
    if (!isYoutube || !playerReadyRef.current) return;
    if (isPlaying) playerRef.current?.playVideo();
    else playerRef.current?.pauseVideo();
  }, [isPlaying, isYoutube]);

  useEffect(() => {
    if (!isHtmlVideo) return undefined;
    let rafId = 0;
    const loop = () => {
      const video = videoElementRef.current;
      const segs = segments;
      if (video && segs && !Number.isNaN(video.currentTime)) {
        const time = video.currentTime;
        setYtTime(time);
        const lastSeg = segs[segs.length - 1];
        if (time >= lastSeg.endSec) {
          video.pause();
          if (Math.abs(video.currentTime - lastSeg.endSec) > 0.05) {
            video.currentTime = lastSeg.endSec;
          }
          setIsPlaying(false);
          setVideoEnded(true);
        } else {
          const idx = segs.findIndex(s => time >= s.startSec && time < s.endSec);
          const nextSlide = idx >= 0 ? idx + 1 : reportedSlideRef.current;
          if (nextSlide !== reportedSlideRef.current) {
            reportedSlideRef.current = nextSlide;
            onSlideClick?.(nextSlide);
          }
        }
      }
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafId);
  }, [isHtmlVideo, segments, onSlideClick]);

  useEffect(() => {
    if (!isHtmlVideo) return;
    const video = videoElementRef.current;
    if (!video) return;
    if (isPlaying) video.play().catch(() => {});
    else video.pause();
  }, [isPlaying, isHtmlVideo]);

  useEffect(() => {
    if (isHtmlVideo && videoElementRef.current) {
      videoElementRef.current.volume = volume;
      videoElementRef.current.muted = volume === 0;
    }
    if (isYoutube && playerRef.current) {
      (playerRef.current as YTPlayer & { setVolume?: (v: number) => void }).setVolume?.(
        volume * 100,
      );
    }
  }, [volume, isHtmlVideo, isYoutube]);

  useEffect(() => {
    if (isHtmlVideo && videoElementRef.current) {
      videoElementRef.current.playbackRate = playbackRate;
    }
    if (isYoutube && playerRef.current) {
      (playerRef.current as YTPlayer & { setPlaybackRate?: (r: number) => void }).setPlaybackRate?.(
        playbackRate,
      );
    }
  }, [playbackRate, isHtmlVideo, isYoutube]);

  useEffect(() => {
    if (!speedMenuOpen) return undefined;
    const handler = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('[data-speed-menu]')) {
        setSpeedMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [speedMenuOpen]);

  const flashAndToggle = (nextPlaying: boolean) => {
    setIsPlaying(nextPlaying);
    setFlashIcon(nextPlaying ? 'play' : 'pause');
    setFlashKey(k => k + 1);
  };

  const handleVideoClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('[data-video-control]')) return;
    if (!hasStarted) return;
    flashAndToggle(!isPlaying);
  };

  return (
    <div
      className="group relative h-[540px] w-[960px] cursor-pointer overflow-hidden rounded-[10px] bg-[#2a2a2a] shadow-[0_1px_1px_-0.1px_rgba(0,0,0,0.05),0_1.98px_3.06px_-0.36px_rgba(0,0,0,0.05),0_5.881px_14.045px_-1.944px_rgba(0,0,0,0.09),0_23.287px_85.958px_-13.997px_rgba(0,0,0,0.19)]"
      onClick={handleVideoClick}
    >
      {isYoutube && (
        <div
          aria-hidden="true"
          className={`pointer-events-none absolute inset-0 transition-opacity duration-300 ${
            hasStarted ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div id={ytContainerId} className="h-full w-full" />
        </div>
      )}

      {isHtmlVideo && (
        <video
          ref={videoElementRef}
          src={videoUrl}
          preload="metadata"
          playsInline
          className="pointer-events-none absolute inset-0 h-full w-full bg-black object-cover"
        />
      )}

      {!isYoutube && !isHtmlVideo && mediaUrls && (
        <img
          key={mediaUrls[currentSlide - 1]}
          src={mediaUrls[currentSlide - 1]}
          alt=""
          className="pointer-events-none absolute inset-0 h-full w-full bg-white object-cover"
        />
      )}

      {(!hasStarted || (!isYoutube && !isHtmlVideo && !mediaUrls)) && (
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(122.7deg, rgba(0,0,0,0.85) 0%, rgba(120,120,120,0.55) 40%, rgba(255,255,255,0.45) 80%)',
          }}
        />
      )}

      {!hasStarted && (
        <div
          key={title}
          className="pointer-events-none absolute bottom-[58px] left-[58px] flex w-[552px] flex-col gap-3 text-white"
          style={{ animation: 'wizard-fadein 200ms ease-out' }}
        >
          <h2 className="text-[48px] font-medium leading-[52px]">{title}</h2>
          <div className="flex items-center gap-2 text-[14px] font-normal leading-[20px]">
            <ClockIcon className="h-4 w-4" />
            <span>{duration}</span>
          </div>
        </div>
      )}

      {isPlaying && subStepTitle && (
        <>
          <div
            key={`gradient-${currentSlide}`}
            className="pointer-events-none absolute inset-0"
            style={{
              backgroundImage:
                'linear-gradient(122.7deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0) 70%)',
              animation: 'substep-reveal 4s ease-out forwards',
            }}
          />
          <div
            key={`title-${currentSlide}-${subStepTitle}`}
            className="pointer-events-none absolute left-[58px] top-[58px] flex flex-col gap-2 text-white"
            style={{ animation: 'substep-reveal 4s ease-out forwards' }}
          >
            {subStepTimestamp && (
              <p className="text-[14px] font-normal leading-[20px]">{subStepTimestamp}</p>
            )}
            <h3 className="text-[40px] font-medium leading-[44px]">{subStepTitle}</h3>
          </div>
        </>
      )}

      {!hasStarted && (
        <button
          type="button"
          aria-label="Play"
          data-video-control
          onClick={() => {
            setHasStarted(true);
            setIsPlaying(true);
          }}
          className="absolute left-1/2 top-1/2 z-10 flex size-[88px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-black/55 text-white shadow-[0_8px_24px_rgba(0,0,0,0.35)] backdrop-blur-sm transition hover:bg-black/65 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
        >
          <svg
            width="22"
            height="28"
            viewBox="0 0 14 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="ml-1"
            aria-hidden="true"
          >
            <path d="M2 1L13 9L2 17V1Z" fill="currentColor" />
          </svg>
        </button>
      )}

      {hasStarted && flashIcon && (
        <div
          key={flashKey}
          className="pointer-events-none absolute left-1/2 top-1/2 z-10 flex items-center justify-center text-white"
          style={{
            animation: 'pause-flash 600ms ease-out forwards',
            filter: 'drop-shadow(0 0 24px rgba(0,0,0,0.55)) drop-shadow(0 0 8px rgba(0,0,0,0.45))',
          }}
          onAnimationEnd={() => setFlashIcon(null)}
        >
          {flashIcon === 'play' ? (
            <svg
              width="36"
              height="48"
              viewBox="0 0 14 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="ml-1"
              aria-hidden="true"
            >
              <path d="M2 1L13 9L2 17V1Z" fill="currentColor" />
            </svg>
          ) : (
            <svg
              width="36"
              height="48"
              viewBox="0 0 14 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <rect x="1" y="1" width="4" height="16" fill="currentColor" />
              <rect x="9" y="1" width="4" height="16" fill="currentColor" />
            </svg>
          )}
        </div>
      )}

      {videoEnded && (
        <>
          <div
            className="pointer-events-none absolute inset-0 z-10 bg-black/60 backdrop-blur-md"
            style={{ animation: 'wizard-fadein 250ms ease-out' }}
          />
          <button
            type="button"
            data-video-control
            onClick={() => {
              if (segments) {
                const startTime = segments[0].startSec;
                if (isHtmlVideo && videoElementRef.current) {
                  videoElementRef.current.currentTime = startTime;
                  videoElementRef.current.play().catch(() => {});
                }
                if (isYoutube && playerRef.current) {
                  playerRef.current.seekTo(startTime, true);
                  playerRef.current.playVideo();
                }
                reportedSlideRef.current = 1;
              }
              setVideoEnded(false);
              setIsPlaying(true);
              onSlideClick?.(1);
            }}
            className="absolute left-1/2 top-1/2 z-20 flex -translate-x-1/2 -translate-y-1/2 items-center gap-2 rounded-[3px] px-3 py-2 text-white transition-[background-color,border-radius] duration-150 hover:rounded-[6px] hover:bg-white/[0.12] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            <RefreshIcon className="h-5 w-5" />
            <span className="text-[18px] font-medium leading-[24px]">Watch again</span>
          </button>
        </>
      )}

      {hasStarted && (
        <>
          <div className="pointer-events-none absolute bottom-0 left-0 right-0 z-30 h-[178px] bg-gradient-to-t from-black/75 to-transparent opacity-0 transition-opacity duration-200 group-hover:opacity-100" />

          <div className="pointer-events-none absolute right-6 bottom-[88px] z-30 rounded-md bg-black/60 px-2 py-1 text-[14px] font-medium leading-[16px] text-white opacity-0 backdrop-blur-sm transition-opacity duration-200 group-hover:opacity-100">
            <span className="font-mono tabular-nums">00:30</span>
            <span className="mx-1 text-white/60">/</span>
            <span className="font-mono tabular-nums text-white/60">
              {duration === '1m' ? '01:00' : duration}
            </span>
          </div>
        </>
      )}

      <div
        className={`absolute inset-x-0 bottom-0 z-30 flex flex-col gap-3 px-m py-3 transition-opacity duration-200 ${
          hasStarted ? 'opacity-0 group-hover:opacity-100' : 'pointer-events-none opacity-0'
        }`}
      >
        <div className="group/timeline flex h-[10px] items-center gap-[2px]">
          {Array.from({ length: slideCount }).map((_, i) => {
            const slideNumber = i + 1;
            const isPast = slideNumber < currentSlide;
            const isCurrent = slideNumber === currentSlide;
            const seg = segments ? segments[i] : null;
            const segDuration = seg ? seg.endSec - seg.startSec : 1;
            const ytProgress =
              hasTimedMedia && seg
                ? Math.max(0, Math.min(1, (ytTime - seg.startSec) / segDuration))
                : 0;
            const animMs = seg ? segDuration * 1000 : SLIDE_DURATION_MS;
            return (
              <button
                key={i}
                type="button"
                aria-label={`Slide ${slideNumber}`}
                data-video-control
                onClick={e => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const fraction = Math.max(
                    0,
                    Math.min(0.999, (e.clientX - rect.left) / rect.width),
                  );
                  if (isYoutube && seg && playerRef.current) {
                    const seekTime = seg.startSec + fraction * segDuration;
                    playerRef.current.seekTo(seekTime, true);
                    playerRef.current.playVideo();
                    reportedSlideRef.current = slideNumber;
                  }
                  if (isHtmlVideo && seg && videoElementRef.current) {
                    const seekTime = seg.startSec + fraction * segDuration;
                    videoElementRef.current.currentTime = seekTime;
                    videoElementRef.current.play().catch(() => {});
                    reportedSlideRef.current = slideNumber;
                  }
                  setSeekTo({ slide: slideNumber, offset: fraction });
                  onSlideClick?.(slideNumber);
                }}
                onMouseMove={e => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  setHoveredCursor({ slide: slideNumber, x: e.clientX - rect.left });
                }}
                onMouseLeave={() => setHoveredCursor(null)}
                style={seg ? { flexGrow: segDuration, flexBasis: 0 } : undefined}
                className={`relative h-[6px] ${seg ? '' : 'flex-1'} rounded-full bg-white/25 transition-[height] duration-150 hover:h-[10px]`}
              >
                {isPast && <span className="absolute inset-0 rounded-full bg-[hsl(219,91%,48%)]" />}
                {isCurrent &&
                  (hasTimedMedia ? (
                    <span
                      className="absolute inset-y-0 left-0 rounded-full bg-[hsl(219,91%,48%)]"
                      style={{ width: `${ytProgress * 100}%` }}
                    >
                      <span className="pointer-events-none absolute right-0 top-1/2 z-10 h-[20px] w-[6px] -translate-y-1/2 translate-x-1/2 rounded-full bg-white opacity-0 shadow-[0_1px_4px_rgba(0,0,0,0.35)] transition-opacity duration-150 group-hover/timeline:opacity-100" />
                    </span>
                  ) : (
                    <span
                      key={`${currentSlide}-${effectiveOffset}-${animMs}`}
                      className="absolute inset-y-0 left-0 rounded-full bg-[hsl(219,91%,48%)]"
                      style={{
                        animation: `slide-progress ${animMs}ms linear forwards`,
                        animationDelay: `-${effectiveOffset * animMs}ms`,
                        animationPlayState: isPlaying ? 'running' : 'paused',
                      }}
                      onAnimationEnd={() => {
                        if (currentSlide < slideCount) {
                          onSlideClick?.(currentSlide + 1);
                        } else {
                          setIsPlaying(false);
                          setVideoEnded(true);
                        }
                      }}
                    >
                      <span className="pointer-events-none absolute right-0 top-1/2 z-10 h-[20px] w-[6px] -translate-y-1/2 translate-x-1/2 rounded-full bg-white opacity-0 shadow-[0_1px_4px_rgba(0,0,0,0.35)] transition-opacity duration-150 group-hover/timeline:opacity-100" />
                    </span>
                  ))}
                {hoveredCursor?.slide === slideNumber && (
                  <span
                    className="pointer-events-none absolute inset-y-0 z-20 w-[2px] -translate-x-1/2 rounded-full bg-white"
                    style={{ left: hoveredCursor.x }}
                  />
                )}
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <PlayerIconButton
              aria-label={isPlaying ? 'Pause' : 'Play'}
              onClick={() => {
                if (!hasStarted) {
                  setHasStarted(true);
                  setIsPlaying(true);
                } else {
                  flashAndToggle(!isPlaying);
                }
              }}
            >
              {isPlaying ? <PauseIcon /> : <PlayIcon />}
            </PlayerIconButton>
            <PlayerIconButton aria-label="Rewind 5 seconds" onClick={() => skip(-5)}>
              <UndoIcon />
            </PlayerIconButton>
            <PlayerIconButton aria-label="Forward 5 seconds" onClick={() => skip(5)}>
              <UndoIcon className="-scale-x-100" />
            </PlayerIconButton>
            <div className="group/volume flex h-9 items-center rounded-[3px] transition-[background-color,border-radius] duration-150 hover:rounded-[6px] hover:bg-white/[0.12]">
              <button
                type="button"
                aria-label={isMuted ? 'Unmute' : 'Mute'}
                onClick={toggleMute}
                data-video-control
                className="flex size-9 items-center justify-center text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                <VolumeIcon muted={isMuted} />
              </button>
              <div
                className="flex h-full w-0 items-center overflow-hidden transition-[width] duration-200 group-hover/volume:w-[88px]"
                data-video-control
              >
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.01}
                  value={volume}
                  onChange={e => handleVolumeChange(parseFloat(e.target.value))}
                  aria-label="Volume level"
                  style={{
                    background: `linear-gradient(to right, white 0%, white ${volume * 100}%, rgba(255,255,255,0.3) ${volume * 100}%, rgba(255,255,255,0.3) 100%)`,
                  }}
                  className="mr-3 block h-[3px] w-[72px] cursor-pointer appearance-none rounded-full outline-none [&::-webkit-slider-thumb]:h-[14px] [&::-webkit-slider-thumb]:w-[14px] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-[0_1px_4px_rgba(0,0,0,0.25)] [&::-moz-range-thumb]:h-[14px] [&::-moz-range-thumb]:w-[14px] [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-white"
                />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <div className="relative" data-speed-menu>
              <button
                type="button"
                aria-label="Playback speed"
                data-video-control
                onClick={() => setSpeedMenuOpen(o => !o)}
                className={`flex h-9 min-w-9 items-center justify-center px-2 text-white transition-[background-color,border-radius] duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white ${
                  speedMenuOpen
                    ? 'rounded-[6px] bg-white/[0.12]'
                    : 'rounded-[3px] hover:rounded-[6px] hover:bg-white/[0.12]'
                }`}
              >
                <span className="text-[14px] font-medium leading-[20px]">
                  {formatRate(playbackRate)}×
                </span>
              </button>
              {speedMenuOpen && (
                <div className="absolute bottom-full right-0 mb-2 flex w-[110px] flex-col gap-0.5 rounded-[6px] bg-black/60 p-1 shadow-[0_8px_24px_rgba(0,0,0,0.35)] backdrop-blur-sm">
                  {PLAYBACK_RATES.map(rate => (
                    <button
                      key={rate}
                      type="button"
                      data-video-control
                      onClick={() => {
                        setPlaybackRate(rate);
                        setSpeedMenuOpen(false);
                      }}
                      className={`flex items-center justify-between gap-3 rounded-[3px] px-3 py-2 text-left text-[14px] font-medium text-white transition-colors hover:bg-white/[0.1] ${
                        rate === playbackRate ? 'bg-white/[0.15]' : ''
                      }`}
                    >
                      <span>{formatRate(rate)}×</span>
                      {rate === playbackRate && <CheckIcon className="h-3 w-3" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <PlayerIconButton aria-label="Captions">
              <CaptionsIcon />
            </PlayerIconButton>
            <PlayerIconButton aria-label="Fullscreen">
              <FullscreenIcon />
            </PlayerIconButton>
          </div>
        </div>
      </div>
    </div>
  );
}

function PlayerIconButton({
  children,
  highlight,
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  children: ReactNode;
  highlight?: boolean;
}) {
  return (
    <button
      type="button"
      data-video-control
      className={`flex size-9 items-center justify-center text-white transition-[background-color,border-radius] duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white ${
        highlight
          ? 'rounded-[6px] bg-white/[0.12]'
          : 'rounded-[3px] hover:rounded-[6px] hover:bg-white/[0.12]'
      } ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}
